/**
 * ERPTable
 *
 * Tabela padronizada do ERP com visual unificado (baseado em stock-risk-table).
 * Toolbar, filtros e botões de ação da página ficam fora deste componente.
 */

import { useState } from 'react'
import { Loader2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import './ERPTable.css'

function sortRows(data, sortColumn, sortDirection, columns) {
  const column = columns.find((col) => col.key === sortColumn)

  return [...data].sort((a, b) => {
    if (column?.sortFn) {
      const comparison = column.sortFn(a, b)
      return sortDirection === 'asc' ? comparison : -comparison
    }

    let aValue = a[sortColumn]
    let bValue = b[sortColumn]

    if (aValue === null || aValue === undefined) {
      return sortDirection === 'asc' ? 1 : -1
    }
    if (bValue === null || bValue === undefined) {
      return sortDirection === 'asc' ? -1 : 1
    }

    const aDate = new Date(aValue)
    const bDate = new Date(bValue)
    if (
      !Number.isNaN(aDate.getTime()) &&
      !Number.isNaN(bDate.getTime()) &&
      (String(aValue).includes('T') || String(aValue).includes('-') || String(aValue).includes('/'))
    ) {
      const comparison = aDate.getTime() - bDate.getTime()
      return sortDirection === 'asc' ? comparison : -comparison
    }

    const aNum = parseFloat(aValue)
    const bNum = parseFloat(bValue)
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && Number.isFinite(aNum) && Number.isFinite(bNum)) {
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
    }

    const comparison = String(aValue)
      .toLowerCase()
      .localeCompare(String(bValue).toLowerCase(), 'pt-BR', { numeric: true, sensitivity: 'base' })
    return sortDirection === 'asc' ? comparison : -comparison
  })
}

function ERPTable({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'Nenhum registro encontrado',
  loadingMessage = 'Carregando dados...',
  onRowClick,
  rowKey,
  pagination,
  minWidth = '720px',
  className = '',
  footerExtra
}) {
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  const handleSort = (column) => {
    if (!column.sortable) return

    if (sortColumn === column.key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column.key)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column.key) return null
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
  }

  const resolveRowKey = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row, index)
    if (row?.id != null) return row.id
    return index
  }

  const sortedData = sortColumn ? sortRows(data, sortColumn, sortDirection, columns) : data

  const {
    page = 1,
    totalPages = 1,
    total = 0,
    onPageChange,
    selectedCount = 0,
    showTotalCount = false
  } = pagination || {}
  const showPagination =
    pagination && (totalPages > 1 || selectedCount > 0 || footerExtra || showTotalCount)

  if (loading && data.length === 0) {
    return (
      <div className={`erp-table-loading ${className}`.trim()}>
        <Loader2 size={28} className="erp-table-spinner" />
        <span>{loadingMessage}</span>
      </div>
    )
  }

  if (!loading && data.length === 0) {
    return (
      <div className={`erp-table-empty ${className}`.trim()}>
        <p>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={`erp-table-wrap ${className}`.trim()}>
      <div className="erp-table-scroll">
        <table className="erp-table" style={{ minWidth }}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={[
                    column.sortable ? 'sortable' : '',
                    column.align === 'center' ? 'text-center' : '',
                    column.align === 'right' ? 'text-right' : '',
                    column.headerClassName || ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ width: column.width }}
                  onClick={() => handleSort(column)}
                >
                  <div className="th-content">
                    <span>{column.label}</span>
                    {column.sortable && <span className="sort-icon">{getSortIcon(column)}</span>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData
              .filter((row) => row && typeof row === 'object')
              .map((row, index) => (
                <tr
                  key={resolveRowKey(row, index)}
                  onClick={(e) => onRowClick?.(row, e)}
                  className={onRowClick ? 'clickable' : ''}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={[
                        column.align === 'center' ? 'text-center' : '',
                        column.align === 'right' ? 'text-right' : '',
                        column.className || ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="erp-table-pagination">
          <span className="erp-table-pagination-info">
            {totalPages > 1 ? `Página ${page} de ${totalPages} · ${total} itens` : `${total} itens`}
            {selectedCount > 0 && ` · ${selectedCount} selecionado(s)`}
            {footerExtra}
          </span>
          {totalPages > 1 && (
            <div className="erp-table-pagination-actions">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => onPageChange?.(page - 1)}
                aria-label="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange?.(page + 1)}
                aria-label="Próxima página"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ERPTable
